/**
 * Restricts numeric input while typing. Fractional digits are truncated to
 * maxDecimals — not rounded. Backend format_field_value uses Decimal.round
 * (:half_up) when values are formatted for display/storage.
 */
function parseMaxDecimals(datasetValue) {
  const parsed = parseInt(datasetValue ?? '0', 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function sanitizeDecimalInput(raw, maxDecimals) {
  let cleaned = raw.replace(/[^\d.-]/g, '');

  let sign = '';
  if (cleaned.startsWith('-')) {
    sign = '-';
    cleaned = cleaned.slice(1);
  }
  cleaned = cleaned.replace(/-/g, '');

  if (maxDecimals === 0) {
    return sign + cleaned.replace(/\./g, '');
  }

  const dotIndex = cleaned.indexOf('.');
  if (dotIndex === -1) {
    return sign + cleaned;
  }

  const intPart = cleaned.slice(0, dotIndex);
  let fracPart = cleaned.slice(dotIndex + 1).replace(/\./g, '');
  fracPart = fracPart.slice(0, maxDecimals);
  const hadTrailingDot = cleaned.endsWith('.');

  if (hadTrailingDot && fracPart === '') {
    return sign + intPart + '.';
  }

  return sign + intPart + '.' + fracPart;
}

function countFractionDigits(value) {
  const dotIndex = value.indexOf('.');
  return dotIndex === -1 ? 0 : value.length - dotIndex - 1;
}

export default {
  DecimalInput: {
    mounted() {
      this.maxDecimals = parseMaxDecimals(this.el.dataset.decimalPlaces);
      this.onInput = () => this.applyFilter();
      this.onPaste = event => this.handlePaste(event);
      this.onKeydown = event => this.handleKeydown(event);

      this.el.addEventListener('input', this.onInput);
      this.el.addEventListener('paste', this.onPaste);
      this.el.addEventListener('keydown', this.onKeydown);
      this.applyFilter();
    },

    updated() {
      const next = parseMaxDecimals(this.el.dataset.decimalPlaces);
      if (next !== this.maxDecimals) {
        this.maxDecimals = next;
      }

      this.applyFilter();
    },

    destroyed() {
      this.el.removeEventListener('input', this.onInput);
      this.el.removeEventListener('paste', this.onPaste);
      this.el.removeEventListener('keydown', this.onKeydown);
    },

    applyFilter() {
      const sanitized = sanitizeDecimalInput(this.el.value, this.maxDecimals);
      if (sanitized !== this.el.value) {
        this.el.value = sanitized;
        this.el.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },

    handlePaste(event) {
      event.preventDefault();

      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      const { selectionStart, selectionEnd, value } = this.el;
      const next = value.slice(0, selectionStart) + pasted + value.slice(selectionEnd);
      const sanitized = sanitizeDecimalInput(next, this.maxDecimals);

      this.el.value = sanitized;
      this.el.dispatchEvent(new Event('input', { bubbles: true }));
    },

    handleKeydown(event) {
      const key = event.key;
      if (typeof key !== 'string') return;

      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      if (allowedKeys.includes(key)) return;
      if (event.ctrlKey || event.metaKey) return;

      if (key === '.' && this.maxDecimals === 0) {
        event.preventDefault();
        return;
      }

      if (key === '.' && this.el.value.includes('.')) {
        const { selectionStart, selectionEnd, value } = this.el;
        const selected = value.slice(selectionStart, selectionEnd);
        if (!selected.includes('.')) {
          event.preventDefault();
          return;
        }
      }

      if (this.maxDecimals > 0 && /^\d$/.test(key)) {
        const { selectionStart, selectionEnd, value } = this.el;
        if (selectionStart !== selectionEnd) return;

        const dotIndex = value.indexOf('.');
        if (dotIndex !== -1 && selectionStart > dotIndex) {
          const fracDigits = countFractionDigits(value);
          if (fracDigits >= this.maxDecimals) {
            event.preventDefault();
            return;
          }
        }
      }

      if (key.length === 1 && !/[\d.-]/.test(key)) {
        event.preventDefault();
      }
    },
  },
};
